import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';
import { fetchCostCentersBulkAdmin } from '~/data/cost-center';
import { fetchCustomFieldsBulkAdmin } from '~/data/custom-field';
import { fetchInvoiceProcessesFromInvoiceIds } from '~/data/invoice-reprocessing';
import { fetchSitesBulkAdmin } from '~/data/site';
import { fetchUsersBulkAdmin } from '~/data/user';

import { isAbortError } from '~/services/kyClient';

import { unique } from '@vestigas/utils';
import { log, newestFirst } from '~/utils';

import { fetchDeliveryNoteChainsAdmin } from '../useQueryDeliveryNoteChainsAdmin';

import { fetchDeliveryNoteAdmin } from './fetchDeliveryNoteAdmin';
import { type DeliveryNoteAdminWithRelatedData } from './types';
import {
  getCreatorId,
  getCustomFieldIds,
  getPermittedCostCenterIds,
  getPermittedSiteIds,
  getProcessStateChanges,
  getReferencedInvoiceIds,
} from './utils';

/**
 * Fetches a delivery note with the given ID along with all related cost centers, sites, and users.
 * @param deliveryNoteId - The ID of the delivery note to fetch
 * @param options - Optional request options (including signal for cancellation)
 * @returns the delivery note data with related cost centers, sites, and users
 * @throws if the API request fails
 */
export const fetchDeliveryNoteAdminWithRelatedData = async (
  deliveryNoteId: UUID,
  options?: KyRequestOptions,
): Promise<DeliveryNoteAdminWithRelatedData> => {
  try {
    // Data fetching step 1: fetch the delivery note
    const deliveryNote = await fetchDeliveryNoteAdmin(deliveryNoteId, options);

    // Data fetching step 2: fetch the asset chains with the asset chain ids from the delivery note
    let assetChains = await fetchDeliveryNoteChainsAdmin(
      [deliveryNoteId],
      options,
    );
    // Reverse the order in which the backend returns the asset chains, because we process them from newest to oldest
    assetChains = assetChains?.length
      ? [...newestFirst(assetChains, 'createdOn')]
      : [];

    const costCenterIds = getPermittedCostCenterIds(deliveryNote);
    const siteIds = getPermittedSiteIds(deliveryNote);

    const processStateChanges = getProcessStateChanges(deliveryNote);

    const processStateChangeUsers = Object.values(
      processStateChanges ?? {},
    ).map(({ userId }) => userId);
    const assetCreators = assetChains.map((chain) =>
      getCreatorId(chain.createdBy),
    );
    const userIds =
      unique([...processStateChangeUsers, ...assetCreators]).filter(Boolean) ??
      [];

    const customFieldIds = getCustomFieldIds(deliveryNote);

    const invoiceIds = getReferencedInvoiceIds(deliveryNote);

    // Data fetching step 3: fetch the cost centers, custom fields, sites, and users (in parallel)
    // with the ids from the delivery note and the asset chain ids.
    const [
      bulkCostCentersResult,
      bulkCustomFieldsResult,
      bulkInvoicesResult,
      bulkSitesResult,
      bulkUsersResult,
    ] = await Promise.allSettled([
      costCenterIds?.length
        ? fetchCostCentersBulkAdmin(costCenterIds, options)
        : Promise.resolve([]),
      customFieldIds?.length
        ? fetchCustomFieldsBulkAdmin(
            {
              ids: customFieldIds,
              includeOptions: true,
            },
            options,
          )
        : Promise.resolve([]),
      invoiceIds?.length
        ? fetchInvoiceProcessesFromInvoiceIds(invoiceIds, options)
        : Promise.resolve([]),
      siteIds?.length
        ? fetchSitesBulkAdmin(siteIds, options)
        : Promise.resolve([]),
      userIds?.length
        ? fetchUsersBulkAdmin(userIds, true, options)
        : Promise.resolve([]),
    ]);

    // Extract values from settled promises, falling back to empty arrays on failure.
    const bulkCostCenters =
      bulkCostCentersResult.status === 'fulfilled'
        ? bulkCostCentersResult.value
        : [];
    const bulkCustomFields =
      bulkCustomFieldsResult.status === 'fulfilled'
        ? bulkCustomFieldsResult.value
        : [];

    const bulkInvoices =
      bulkInvoicesResult.status === 'fulfilled' ? bulkInvoicesResult.value : [];
    const bulkSites =
      bulkSitesResult.status === 'fulfilled' ? bulkSitesResult.value : [];
    const bulkUsers =
      bulkUsersResult.status === 'fulfilled' ? bulkUsersResult.value : [];

    // Log any failed requests for debugging purposes (skip AbortError as it's expected)
    if (
      bulkCostCentersResult.status === 'rejected' &&
      !isAbortError(bulkCostCentersResult.reason)
    ) {
      log.error({
        message:
          'Failed to fetch cost centers in bulk when fetching delivery note admin with related data',
        error: bulkCostCentersResult.reason,
      });
    }

    if (
      bulkCustomFieldsResult.status === 'rejected' &&
      !isAbortError(bulkCustomFieldsResult.reason)
    ) {
      log.error({
        message:
          'Failed to fetch custom fields in bulk when fetching delivery note admin with related data',
        error: bulkCustomFieldsResult.reason,
      });
    }

    if (
      bulkInvoicesResult.status === 'rejected' &&
      !isAbortError(bulkInvoicesResult.reason)
    ) {
      log.error({
        message:
          'Failed to fetch invoices in bulk when fetching delivery note admin with related data',
        error: bulkInvoicesResult.reason,
      });
    }

    if (
      bulkSitesResult.status === 'rejected' &&
      !isAbortError(bulkSitesResult.reason)
    ) {
      log.error({
        message:
          'Failed to fetch sites in bulk when fetching delivery note admin with related data',
        error: bulkSitesResult.reason,
      });
    }

    if (
      bulkUsersResult.status === 'rejected' &&
      !isAbortError(bulkUsersResult.reason)
    ) {
      log.error({
        message:
          'Failed to fetch users in bulk when fetching delivery note admin with related data',
        error: bulkUsersResult.reason,
      });
    }

    return {
      assetChains,
      bulkCostCenters,
      bulkCustomFields,
      bulkInvoices,
      bulkSites,
      bulkUsers,
      deliveryNote,
      processStateChanges,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Note Admin',
      error,
    });

    // Re-throw the error to propagate it to the calling function
    throw error;
  }
};
